import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import TrackingMap from "@/components/TrackingMap";
import DateTimePicker from "@/components/DateTimePicker";
import {
  STATUS_CONFIG, STATUS_FLOW, CATEGORY_CONFIG, PRIORITY_CONFIG, OPS_ROLES,
  fmtDateTime, fmtDate, ROLE_LABELS,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, User, Truck, Phone, Mail, Clock, FileText, Copy, ExternalLink,
  Pencil, Trash2, CalendarClock, CheckCircle2, Navigation, Loader2, Download, Building2, Upload, Paperclip, History, Package,
} from "lucide-react";

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Field({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-stark-muted" />}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">{label}</div>
        <div className="text-sm font-medium text-stark-ink">{value}</div>
      </div>
    </div>
  );
}

function Timeline({ job }) {
  const times = {
    assigned: job.created_at,
    checked_in: job.checked_in_at,
    en_route: job.departed_at,
    arrived: job.arrived_at,
    completed: job.completed_at,
  };
  const currentIdx = STATUS_FLOW.indexOf(job.status);
  return (
    <div className="space-y-0">
      {STATUS_FLOW.map((s, i) => {
        const done = times[s] || (currentIdx >= 0 && i <= currentIdx);
        const cfg = STATUS_CONFIG[s];
        const isLast = i === STATUS_FLOW.length - 1;
        return (
          <div key={s} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 ${done ? "border-stark-terracotta bg-stark-terracotta text-white" : "border-stark-border bg-white text-stark-border-strong"}`}>
                {done ? <CheckCircle2 className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
              </div>
              {!isLast && <div className={`h-8 w-0.5 ${done ? "bg-stark-terracotta/40" : "bg-stark-border"}`} />}
            </div>
            <div className="pb-2">
              <div className={`text-sm font-semibold ${done ? "text-stark-ink" : "text-stark-muted"}`}>{cfg.label}</div>
              <div className="text-xs text-stark-muted">{times[s] ? fmtDateTime(times[s]) : "—"}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reOpen, setReOpen] = useState(false);
  const [reAt, setReAt] = useState("");
  const [reWindow, setReWindow] = useState(60);
  const [reReason, setReReason] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadKind = useRef("job");

  const load = useCallback(() => {
    api.get(`/deliveries/${id}`).then(({ data }) => setJob(data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(load, [load]);

  const isOps = OPS_ROLES.includes(user.role);

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${id}/status`, { status, ...extra });
      setJob(data);
      toast.success(`Marked ${STATUS_CONFIG[status].label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const startRoute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${id}/start`);
      setJob(data);
      toast.success("Route started — customer notified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doReschedule = async () => {
    if (!reAt) return toast.error("Pick a new time");
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${id}/reschedule`, {
        scheduled_at: reAt + ":00",
        window_minutes: Number(reWindow),
        reason: reReason || undefined,
      });
      setJob(data);
      setReOpen(false);
      toast.success("Rescheduled — customer notified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/deliveries/${id}`);
      toast.success("Job deleted");
      navigate("/jobs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copyTracking = () => {
    if (!job.tracking_url) return;
    navigator.clipboard.writeText(job.tracking_url);
    toast.success("Tracking link copied");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max file size is 5 MB");
    const kind = uploadKind.current;
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/deliveries/${id}/documents`, {
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        data_base64: b64,
        kind,
      });
      toast.success(kind === "completion" ? "Completion doc uploaded" : "Document uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const pickUpload = (kind) => { uploadKind.current = kind; fileRef.current?.click(); };

  const downloadDoc = async (doc) => {
    try {
      const { data } = await api.get(`/deliveries/${id}/documents/${doc.id}`);
      const bytes = atob(data.data_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: data.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/deliveries/${id}/documents/${docId}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl">
        <button onClick={() => navigate("/jobs")} className="mb-5 flex items-center gap-1.5 text-sm font-semibold text-stark-muted hover:text-stark-ink">
          <ArrowLeft className="h-4 w-4" /> Back to jobs
        </button>
        <div className="stark-card flex flex-col items-center justify-center py-20 text-center">
          <MapPin className="h-10 w-10 text-stark-border-strong" />
          <p className="mt-3 text-lg font-bold text-stark-ink">Job not found</p>
          <p className="text-sm text-stark-muted">It may have been deleted or the link is invalid.</p>
          <Button className="mt-5 rounded-xl bg-stark-terracotta hover:bg-[#bd4f2c]" onClick={() => navigate("/jobs")}>View all jobs</Button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stark-terracotta" />
      </div>
    );
  }

  const cat = CATEGORY_CONFIG[job.category] || { label: job.category };
  const prio = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;
  const terminal = ["completed", "cancelled"].includes(job.status);

  return (
    <div className="mx-auto max-w-5xl">
      <button onClick={() => navigate(-1)} className="mb-5 flex items-center gap-1.5 text-sm font-semibold text-stark-muted hover:text-stark-ink" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="stark-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-stark-tint px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-stark-terracotta">{cat.label}</span>
              <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: prio.color }}>{prio.label} priority</span>
              {job.gated_property && <span className="rounded-md bg-stark-warning/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-stark-warning">Gated</span>}
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-stark-ink sm:text-3xl">{job.customer_name}</h1>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm text-stark-muted">
              <MapPin className="h-4 w-4" /> {job.address}
            </div>
          </div>
          <StatusBadge status={job.status} testId="detail-status-badge" />
        </div>

        {isOps && (
          <div className="mt-5 flex flex-wrap gap-2 border-t border-stark-border pt-5">
            <Button variant="outline" className="rounded-xl" onClick={() => navigate(`/jobs/${id}/edit`)} data-testid="edit-job-btn">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
            {!terminal && (
              <Button variant="outline" className="rounded-xl" onClick={() => { setReAt(""); setReOpen(true); }} data-testid="reschedule-btn">
                <CalendarClock className="mr-1.5 h-4 w-4" /> Reschedule
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-xl text-stark-error hover:text-stark-error" data-testid="delete-job-btn">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this job?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes the job and its tracking history.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-stark-error hover:bg-stark-error/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        {/* Left: details */}
        <div className="space-y-5 lg:col-span-2">
          {/* Map */}
          {(job.status === "en_route" || job.tracking_token) && (
            <div className="stark-card overflow-hidden p-0">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-2 text-sm font-bold text-stark-ink">
                  <Navigation className="h-4 w-4 text-stark-terracotta" /> Live location
                </div>
                {job.tracking_url && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={copyTracking} data-testid="copy-tracking-btn"><Copy className="h-4 w-4" /></Button>
                    <a href={`/track/${job.tracking_token}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                    </a>
                  </div>
                )}
              </div>
              <div className="px-4 pb-4">
                <TrackingMap dest={{ lat: job.dest_lat, lng: job.dest_lng }} customerName={job.customer_name} height={280} />
              </div>
            </div>
          )}

          <div className="stark-card p-6">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-stark-muted">Job details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Scheduled" value={fmtDateTime(job.scheduled_at)} icon={Clock} />
              <Field label="Arrival window" value={`± ${job.window_minutes} min`} icon={Clock} />
              {job.estimated_duration_min && <Field label="Est. duration" value={`${job.estimated_duration_min} min`} icon={Clock} />}
              {job.multi_day && <Field label="Multi-day" value={`Until ${fmtDate(job.end_at)}`} icon={CalendarClock} />}
              <Field label="PO number" value={job.po_number} icon={FileText} />
              <Field label="Job source" value={job.job_source} icon={Building2} />
              <Field label="Agent / Rep" value={job.agent_rep} icon={User} />
              <Field label="Account Manager" value={job.am_name || job.salesperson_name} icon={User} />
            </div>
            {job.job_description && (
              <div className="mt-4 border-t border-stark-border pt-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">Description</div>
                <p className="mt-1 text-sm text-stark-ink">{job.job_description}</p>
              </div>
            )}
            {job.notes_for_techs && (
              <div className="mt-4 rounded-xl bg-stark-tint p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-terracotta">Notes for crew</div>
                <p className="mt-1 text-sm text-stark-ink">{job.notes_for_techs}</p>
              </div>
            )}
            {job.completion_notes && (
              <div className="mt-4 rounded-xl bg-stark-success/10 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-success">Completion notes</div>
                <p className="mt-1 text-sm text-stark-ink">{job.completion_notes}</p>
              </div>
            )}
          </div>

          {job.items && job.items.length > 0 && (
            <div className="stark-card p-6" data-testid="job-items-card">
              <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-stark-muted">
                <Package className="h-4 w-4" /> Items to deliver
                <span className="rounded-full bg-stark-tint px-2 py-0.5 text-[11px] font-bold text-stark-terracotta">{job.items.length}</span>
              </h2>
              <div className="divide-y divide-stark-border overflow-hidden rounded-xl border border-stark-border">
                {job.items.map((it, i) => (
                  <div key={i} className="flex items-start gap-3 p-3" data-testid={`job-item-${i}`}>
                    {it.quantity ? (
                      <span className="mt-0.5 flex h-6 min-w-[1.75rem] shrink-0 items-center justify-center rounded-md bg-stark-terracotta px-1.5 text-xs font-bold text-white">{it.quantity}</span>
                    ) : (
                      <Package className="mt-0.5 h-5 w-5 shrink-0 text-stark-terracotta" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-stark-ink">{it.name}</div>
                      {it.note && <div className="text-xs text-stark-muted">{it.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="stark-card p-6">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-stark-muted">Contact & assignment</h2>            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Customer email" value={job.customer_email} icon={Mail} />
              <Field label="Customer phone" value={job.customer_phone} icon={Phone} />
              {job.contact?.name && <Field label={`On-site contact${job.contact.role ? ` (${job.contact.role})` : ""}`} value={job.contact.name} icon={User} />}
              {job.contact?.phone && <Field label="Contact phone" value={job.contact.phone} icon={Phone} />}
              <Field label="Crew" value={job.assigned_tech_names?.join(", ")} icon={User} />
              <Field label="Truck" value={job.truck_name} icon={Truck} />
            </div>
          </div>

          {/* Documents */}
          <div className="stark-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-stark-muted">
                <Paperclip className="h-4 w-4" /> Documents
                <span className="rounded-full bg-stark-tint px-2 py-0.5 text-[11px] font-bold text-stark-terracotta">{job.documents?.length || 0}</span>
              </h2>
              <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="doc-file-input" />
              <div className="flex gap-2">
                {isOps && (
                  <Button size="sm" className="rounded-xl bg-stark-terracotta hover:bg-[#bd4f2c]" disabled={uploading} onClick={() => pickUpload("job")} data-testid="doc-upload-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1.5 h-4 w-4" /> Upload</>}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-xl" disabled={uploading} onClick={() => pickUpload("completion")} data-testid="doc-upload-completion-btn">
                  <Upload className="mr-1.5 h-4 w-4" /> Completion
                </Button>
              </div>
            </div>
            {(!job.documents || job.documents.length === 0) ? (
              <div className="rounded-xl border border-dashed border-stark-border py-8 text-center">
                <FileText className="mx-auto h-8 w-8 text-stark-border-strong" />
                <p className="mt-2 text-sm text-stark-muted">No LWO / DWO or completion files yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {job.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 rounded-xl border border-stark-border p-3" data-testid={`doc-row-${doc.id}`}>
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${doc.kind === "completion" ? "bg-stark-success/15 text-stark-success" : "bg-stark-tint text-stark-terracotta"}`}><FileText className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-stark-ink">{doc.filename}</span>
                        {doc.kind === "completion" && <span className="rounded bg-stark-success/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-stark-success">Completion</span>}
                      </div>
                      <div className="text-xs text-stark-muted">{fmtBytes(doc.size)}{doc.uploader_name ? ` · ${doc.uploader_name}` : ""}</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(doc)} data-testid={`doc-download-${doc.id}`} title="Download"><Download className="h-4 w-4" /></Button>
                    {isOps && (
                      <Button size="sm" variant="ghost" className="text-stark-error" onClick={() => deleteDoc(doc.id)} data-testid={`doc-delete-${doc.id}`} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: timeline + actions */}
        <div className="space-y-5">
          <div className="stark-card p-6">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-stark-muted">Status timeline</h2>
            <Timeline job={job} />
          </div>

          {!terminal && (
            <div className="stark-card p-6">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-stark-muted">Quick actions</h2>
              <div className="space-y-2.5">
                {job.status === "assigned" && (
                  <Button className="w-full rounded-xl bg-stark-moss font-bold hover:bg-stark-moss/90" disabled={busy} onClick={() => setStatus("checked_in")} data-testid="action-checkin">
                    Check In
                  </Button>
                )}
                {["assigned", "checked_in"].includes(job.status) && (
                  <Button className="w-full rounded-xl bg-stark-terracotta font-bold hover:bg-[#bd4f2c]" disabled={busy} onClick={startRoute} data-testid="action-start">
                    <Navigation className="mr-1.5 h-4 w-4" /> Start Route
                  </Button>
                )}
                {job.status === "en_route" && (
                  <Button className="w-full rounded-xl bg-stark-success font-bold hover:bg-stark-success/90" disabled={busy} onClick={() => setStatus("arrived")} data-testid="action-arrived">
                    Mark Arrived
                  </Button>
                )}
                {["en_route", "arrived"].includes(job.status) && (
                  <Button className="w-full rounded-xl bg-stark-ink font-bold text-white hover:bg-stark-ink/90" disabled={busy} onClick={() => setStatus("completed")} data-testid="action-complete">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete Job
                  </Button>
                )}
                {isOps && (
                  <Button variant="outline" className="w-full rounded-xl text-stark-error hover:text-stark-error" disabled={busy} onClick={() => setStatus("cancelled")} data-testid="action-cancel">
                    Cancel Job
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Activity log */}
          <div className="stark-card p-6" data-testid="activity-log">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-stark-muted">
              <History className="h-4 w-4" /> Activity log
            </h2>
            {(!job.activity_log || job.activity_log.length === 0) ? (
              <p className="text-sm text-stark-muted">No activity recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {[...job.activity_log].reverse().map((a, i) => (
                  <div key={i} className="flex gap-3" data-testid={`activity-${i}`}>
                    <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-stark-terracotta" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-stark-ink">
                        {a.actor_name || "Someone"} <span className="font-normal text-stark-muted">· {a.action.replace(/_/g, " ")}</span>
                      </div>
                      {a.detail && <div className="text-xs text-stark-muted">{a.detail}</div>}
                      <div className="text-[11px] text-stark-muted">{fmtDateTime(a.at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={reOpen} onOpenChange={setReOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stark-muted">Arrival window (min)</label>
              <Input type="number" value={reWindow} onChange={(e) => setReWindow(e.target.value)} data-testid="reschedule-window-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stark-muted">Reason (optional)</label>
              <Textarea value={reReason} onChange={(e) => setReReason(e.target.value)} rows={2} data-testid="reschedule-reason-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stark-muted">New date & time</label>
              <DateTimePicker testId="reschedule-datetime" value={reAt} onChange={setReAt} placeholder="Select date & time" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReOpen(false)} data-testid="reschedule-cancel">Cancel</Button>
            <Button className="bg-stark-terracotta hover:bg-[#bd4f2c]" disabled={busy} onClick={doReschedule} data-testid="reschedule-confirm">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
